// src/blogs/HeadModel.jsx
// The animated head mesh itself, used by both HeadScene (the full-size,
// arrow-driven head) and FloatingMiniHead (the small top-left nav head shown
// on every blog scene). Loading/fitting/constants live in headModelUtils.js.
import React, { useEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import { useFrame } from "@react-three/fiber";
import { useGLTF } from "@react-three/drei";

import {
  HEAD_URL,
  HEAD_SIZE,
  HEAD_YAW_OFFSET,
  STEP,
  SPIN_DAMP,
  YAW_SIGN,
  PITCH_SIGN,
  useFittedModel,
} from "./headModelUtils.js";

const IDLE_COLOR = "#ffffff";
const HOVER_COLOR = "#eaf6ff";
const HOVER_SCALE = 1.05;

/**
 * The head model, damped toward a yaw/pitch quarter-turn target and gently
 * bobbing. Optionally interactive — pass `onSelect` to make it clickable,
 * with the same hover/cursor idiom HeadScene's Arrow uses (hover state in a
 * ref, damped scale/color feedback read in useFrame).
 */
export function Head({
  yaw,
  pitch,
  size = HEAD_SIZE,
  dampSpeed = SPIN_DAMP,
  float = true,
  onSelect,
}) {
  const material = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: IDLE_COLOR,
        roughness: 0.62,
        metalness: 0.04,
      }),
    []
  );
  useEffect(() => () => material.dispose(), [material]);

  const { object, scale, offset } = useFittedModel(HEAD_URL, size, material);

  const scaleRef = useRef(null);
  const pitchRef = useRef(null);
  const yawRef = useRef(null);
  const hoveredRef = useRef(false);
  const swatch = useMemo(() => new THREE.Color(), []);

  const interactive = typeof onSelect === "function";

  useEffect(
    () => () => {
      document.body.style.cursor = "";
    },
    []
  );

  useFrame((state, dt) => {
    const step = Math.min(dt, 0.1);

    // Yaw sits inside pitch, so the up arrow always tips the head toward the
    // camera regardless of which way it is currently facing.
    if (yawRef.current) {
      yawRef.current.rotation.y = THREE.MathUtils.damp(
        yawRef.current.rotation.y,
        HEAD_YAW_OFFSET + yaw * STEP * YAW_SIGN,
        dampSpeed,
        step
      );
    }

    if (pitchRef.current) {
      pitchRef.current.rotation.x = THREE.MathUtils.damp(
        pitchRef.current.rotation.x,
        pitch * STEP * PITCH_SIGN,
        dampSpeed,
        step
      );
      if (float) {
        pitchRef.current.position.y = Math.sin(state.clock.elapsedTime * 0.5) * 0.03;
      }
    }

    if (scaleRef.current) {
      const target = interactive && hoveredRef.current ? HOVER_SCALE : 1;
      const next = THREE.MathUtils.damp(scaleRef.current.scale.x, target, 9, step);
      scaleRef.current.scale.setScalar(next);
    }

    if (interactive) {
      const targetColor = hoveredRef.current ? HOVER_COLOR : IDLE_COLOR;
      material.color.lerp(swatch.set(targetColor), 1 - Math.exp(-9 * step));
    }
  });

  const setHovered = (value) => {
    if (!interactive) return;
    hoveredRef.current = value;
    document.body.style.cursor = value ? "pointer" : "";
  };

  return (
    <group ref={scaleRef}>
      <group
        ref={pitchRef}
        onClick={
          interactive
            ? (event) => {
                event.stopPropagation();
                onSelect();
              }
            : undefined
        }
        onPointerOver={
          interactive
            ? (event) => {
                event.stopPropagation();
                setHovered(true);
              }
            : undefined
        }
        onPointerOut={interactive ? () => setHovered(false) : undefined}
      >
        <group ref={yawRef} rotation={[0, HEAD_YAW_OFFSET, 0]}>
          <group position={offset} scale={scale}>
            <primitive object={object} />
          </group>
        </group>
      </group>
    </group>
  );
}

useGLTF.preload(HEAD_URL);
